import React from 'react';
import type { EnrollmentStatus } from '../types';

interface Step {
  key: EnrollmentStatus;
  label: string;
  shortLabel: string;
}

const STEPS_ONLINE: Step[] = [
  { key: 'portal_registered', label: 'Iscrizione ricevuta', shortLabel: 'Iscrizione' },
  { key: 'lms_enrolled', label: 'Confermata', shortLabel: 'Confermata' },
  { key: 'zoom_registration_pending', label: 'Registrazione Zoom', shortLabel: 'Zoom reg.' },
  { key: 'zoom_registered', label: 'Zoom completato', shortLabel: 'Zoom ok' },
  { key: 'zoom_approved', label: 'Approvato', shortLabel: 'Approvato' },
  { key: 'join_link_sent', label: 'Link inviato', shortLabel: 'Link' },
  { key: 'attended', label: 'Partecipato', shortLabel: 'Presente' },
];

const STEPS_IN_PRESENZA: Step[] = [
  { key: 'portal_registered', label: 'Iscrizione ricevuta', shortLabel: 'Iscrizione' },
  { key: 'lms_enrolled', label: 'Confermata', shortLabel: 'Confermata' },
  { key: 'attended', label: 'Partecipato', shortLabel: 'Presente' },
];

// Order of statuses for step calculation
const STATUS_ORDER: Record<EnrollmentStatus, number> = {
  waitlisted: -1,
  portal_registered: 0,
  lms_enrolled: 1,
  zoom_registration_pending: 2,
  zoom_registered: 3,
  zoom_approved: 4,
  join_link_sent: 5,
  attended: 6,
  no_show: 6,
  cancelled: -1,
};

// Simplified order for in-presence courses
const STATUS_ORDER_IN_PRESENZA: Record<EnrollmentStatus, number> = {
  waitlisted: -1,
  portal_registered: 0,
  lms_enrolled: 1,
  zoom_registration_pending: 1,
  zoom_registered: 1,
  zoom_approved: 1,
  join_link_sent: 1,
  attended: 2,
  no_show: 2,
  cancelled: -1,
};

interface StepIndicatorProps {
  currentStatus: EnrollmentStatus;
  inPresenza?: boolean;
}

export default function StepIndicator({ currentStatus, inPresenza = false }: StepIndicatorProps) {
  const isCancelled = currentStatus === 'cancelled';
  const isNoShow = currentStatus === 'no_show';

  const steps = inPresenza ? STEPS_IN_PRESENZA : STEPS_ONLINE;
  const orderMap = inPresenza ? STATUS_ORDER_IN_PRESENZA : STATUS_ORDER;
  const currentOrder = orderMap[currentStatus] ?? 0;

  if (isCancelled) {
    return (
      <div style={{ textAlign: 'center', padding: '1.5rem', background: 'var(--danger-light)', borderRadius: 'var(--border-radius)', color: '#b91c1c', fontWeight: 600 }}>
        Iscrizione annullata
      </div>
    );
  }

  if (isNoShow) {
    return (
      <div style={{ textAlign: 'center', padding: '1.5rem', background: 'var(--warning-light)', borderRadius: 'var(--border-radius)', color: '#92400e', fontWeight: 600 }}>
        Non presente alla sessione
      </div>
    );
  }

  return (
    <div className="steps">
      {steps.map((step, index) => {
        const stepOrder = orderMap[step.key];
        const isCompleted = stepOrder < currentOrder;
        const isCurrent = step.key === currentStatus ||
          (inPresenza && currentStatus !== 'attended' && step.key === 'lms_enrolled' &&
            ['zoom_registration_pending', 'zoom_registered', 'zoom_approved', 'join_link_sent'].includes(currentStatus));

        let className = 'step';
        if (isCompleted) className += ' completed';
        else if (isCurrent) className += ' current';

        return (
          <div key={step.key} className={className}>
            <div className="step-dot">{isCompleted ? '✓' : index + 1}</div>
            <div className="step-label">{step.shortLabel}</div>
          </div>
        );
      })}
    </div>
  );
}
